import React, { useState, useEffect } from "react";
import { Loader2, Play, CheckCircle2, AlertCircle } from "lucide-react";
import { runScan, getScan } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

export function RunScanButton({ onComplete }) {
  const [scan, setScan] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!scan || !running) return;
    const timer = setInterval(async () => {
      try {
        const fresh = await getScan(scan.id);
        setScan(fresh);
        if (fresh.status !== "running") {
          setRunning(false);
          clearInterval(timer);
          if (fresh.status === "completed") {
            const parts = [];
            if (fresh.dcc_imported_new)
              parts.push(`${fresh.dcc_imported_new.toLocaleString()} new from DCC`);
            if (fresh.dcc_merged)
              parts.push(`${fresh.dcc_merged.toLocaleString()} DCC-merged`);
            if (fresh.dcc_license_alerts)
              parts.push(`${fresh.dcc_license_alerts} license alert${fresh.dcc_license_alerts === 1 ? "" : "s"}`);
            parts.push(`${fresh.new_dates_found} new opening date${fresh.new_dates_found === 1 ? "" : "s"}`);
            toast.success("Scan complete", {
              description: parts.join(" · "),
              duration: 8000,
            });
            onComplete?.(fresh);
          } else {
            toast.error("Scan failed", { description: fresh.message });
          }
        }
      } catch (e) {
        // ignore transient errors
      }
    }, 1500);
    return () => clearInterval(timer);
  }, [scan, running, onComplete]);

  const handleClick = async () => {
    if (running) return;
    try {
      setRunning(true);
      const s = await runScan();
      setScan(s);
      toast.info("Scan started", {
        description: "Fetching latest DCC license list, then scanning for opening dates…",
      });
    } catch (e) {
      setRunning(false);
      toast.error("Could not start scan", { description: e?.message });
    }
  };

  const pct = scan && scan.total ? Math.round((scan.processed / scan.total) * 100) : 0;

  return (
    <div className="flex items-center gap-3">
      <Button
        data-testid="run-scan-btn"
        onClick={handleClick}
        disabled={running}
        className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 font-medium rounded-md transition-colors duration-150"
      >
        {running ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Scanning… {pct}%
          </>
        ) : (
          <>
            <Play className="w-4 h-4 mr-2" />
            Run Scan
          </>
        )}
      </Button>

      {scan && !running && scan.status === "completed" && (
        <span
          data-testid="scan-complete-indicator"
          className="inline-flex items-center gap-1 text-xs text-emerald-400 font-mono"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          {scan.new_dates_found} new
        </span>
      )}
      {scan && !running && scan.status === "failed" && (
        <span className="inline-flex items-center gap-1 text-xs text-rose-400">
          <AlertCircle className="w-3.5 h-3.5" />
          Failed
        </span>
      )}
    </div>
  );
}

export default RunScanButton;
